import React, { ReactNode } from 'react';
import { Flex } from 'glints-aries/es';
import { Icon, Typography, useModal } from 'glints-aries/es/@next';
import { Neutral } from 'glints-aries/es/@next/utilities/colors';
import {
  space4,
  space8,
  space12,
  space16,
  space24,
} from 'glints-aries/es/@next/utilities/spacing';
import {
  defineMessages,
  FormattedDate,
  FormattedMessage,
  MessageDescriptor,
} from 'react-intl';
import styled, { createGlobalStyle } from 'styled-components';

import useOpenChatSupport from '../../../../common/hooks/useOpenChatSupport';
import { useCompanyVerificationModalsContext } from '../../../CompanyVerificationModals/CompanyVerificationModalsProvider';

const messages = defineMessages({
  reviewProcessTypicallyTakes: {
    id: 'text-review-process-typically-takes',
    defaultMessage: '<b>Review Process:</b> Typically takes 1-2 business days.',
  },
  youWillReceiveNotifi: {
    id: 'text-notification-you-will-receive-an',
    defaultMessage:
      "<b>Notification:</b> You'll receive an email once verified.",
  },
  accessUponVerification: {
    id: 'text-access-upon-verification-you-will',
    defaultMessage:
      "<b>Access:</b> Upon verification, you'll have full access to all employer features.",
  },
  jobNewlyCreated: {
    id: 'text-job-your-newly-created',
    defaultMessage:
      '<b>Job:</b> Your newly created job will automatically be posted',
  },
});
const nextStepsMessages: MessageDescriptor[] = [
  messages.reviewProcessTypicallyTakes,
  messages.youWillReceiveNotifi,
  messages.accessUponVerification,
  messages.jobNewlyCreated,
];

const nextStepsMessagesForTierUpgrade: MessageDescriptor[] = [
  messages.reviewProcessTypicallyTakes,
  messages.youWillReceiveNotifi,
];

const CloseIconWrapper = styled(Flex)`
  justify-content: flex-end;
  position: absolute;
  top: 0;
  right: 0;
`;
const ListItem = styled(Typography)`
  ::before {
    content: '\u2022';
    padding-inline-start: 10px;
    margin-right: ${space8};
  }
`;
const NextStepContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${space16};
  background-color: ${Neutral.B99};
  gap: ${space4};
`;
const ModalOverrides = createGlobalStyle`
  .modal-actions-button-group {
    width: 100%;
    justify-content: space-between;
  }
`;

type ModalProps = {
  submittedDate: string;
  isTierUpgrade: boolean;
};
function ModalContent({
  isTierUpgrade,
  submittedDate,
  closeModal,
}: ModalProps & { closeModal: () => void }) {
  const nextStepOptions = isTierUpgrade
    ? nextStepsMessagesForTierUpgrade
    : nextStepsMessages;
  return (
    <>
      <div style={{ position: 'relative' }}>
        <CloseIconWrapper>
          <Icon
            style={{
              cursor: 'pointer',
            }}
            name="ri-close"
            onClick={closeModal}
            fill={Neutral.B40}
            width={space24}
          />
        </CloseIconWrapper>
      </div>
      <Flex flexDirection="column" gap={space12}>
        <img
          src="images/tier-verification/verification-in-progress-badge.svg"
          style={{
            width: 80,
            height: 80,
          }}
        />
        <Flex flexDirection="column" gap={space16}>
          <Flex flexDirection="column" gap={space4}>
            <Typography variant="subtitle1" color={Neutral.B18}>
              <FormattedMessage
                id="text-company-verification-submitted"
                defaultMessage="Company Verification Submitted"
              />
            </Typography>
            <Typography variant="body1" color={Neutral.B18}>
              <FormattedMessage
                id="text-thank-you-for-submitting"
                defaultMessage="Thank you for submitting your company information. Our team is reviewing your details for accuracy and compliance."
              />
            </Typography>
          </Flex>

          <Typography variant="body2" color={Neutral.B18}>
            <FormattedMessage
              id="text-submission-date"
              defaultMessage="Submission Date: {date}"
              values={{
                date: (
                  <FormattedDate
                    value={submittedDate}
                    year="numeric"
                    month="short"
                    day="2-digit"
                  />
                ),
              }}
            />
          </Typography>
          <NextStepContainer>
            <Typography variant="subtitle2" color={Neutral.B40}>
              <FormattedMessage
                id="text-next-steps"
                defaultMessage="Next Steps"
              />
            </Typography>
            {nextStepOptions.map(message => (
              <ListItem variant="body1" color={Neutral.B18} key={message.id}>
                <FormattedMessage
                  {...message}
                  values={{
                    b: (...chunks: ReactNode[]) => (
                      <Typography variant="body2" color={Neutral.B18} as="span">
                        {chunks}
                      </Typography>
                    ),
                  }}
                />
              </ListItem>
            ))}
          </NextStepContainer>

          <Typography variant="body1" color={Neutral.B18}>
            <FormattedMessage
              id="text-if-you-need-further"
              defaultMessage="If you need further assistance, please reach out to our support team at <link1>hello@glints.com</link1>"
              values={{
                link1: (chunks: ReactNode[]) => (
                  <a href={`mailto:${chunks}`}>{chunks}</a>
                ),
              }}
            />
          </Typography>
        </Flex>
      </Flex>
      <ModalOverrides />
    </>
  );
}

export default function useCompanyVerificationSubmittedModal() {
  const { open, close } = useModal();
  const openChatSupport = useOpenChatSupport();
  const { setSubmittedModalOpen } = useCompanyVerificationModalsContext();
  const closeModal = () => {
    close();
    setSubmittedModalOpen(false);
  };
  const openModal = (props: ModalProps) => {
    setSubmittedModalOpen(true);
    open({
      primaryAction: {
        onClick: closeModal,
        label: (
          <Typography variant="button">
            <FormattedMessage
              id="interactive-continue-to-dashboard"
              defaultMessage="Continue to Dashboard"
            />
          </Typography>
        ),
      },
      secondaryAction: {
        onClick: openChatSupport,
        label: (
          <Typography variant="button">
            <FormattedMessage
              id="interactive-contact-us"
              defaultMessage="Contact Us"
            />
          </Typography>
        ),
      },
      children: <ModalContent {...props} closeModal={closeModal} />,
      style: {
        width: '620px',
      },
      onClose: closeModal,
      closeOnClickOutside: true,
    });
  };

  return {
    openModal,
  };
}
